import { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { isUndefined } from 'lodash';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';

import { Context as NavigationContext } from '@swf/components/StarsWizard/components/Navigation/Navigation';
import useFromEvent from '@swf/core/hooks/useFromEvent';
import useModal from '@swf/core/hooks/useModal';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import useWizardExit from '@swf/core/hooks/useWizardExit';
import useWizardLoading from '@swf/core/hooks/useWizardLoading';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { TitleProp } from '@swf/core/types/Props';

import trPromptVideo from '../../../../assets/video/tr-prompt-video.mp4';
import trScreenVideo from '../../../../assets/video/tr-screen-video.mp4';
import trVideoMobile from '../../../../assets/video/tr-video-mobile.mp4';
import { executeHistoryPush } from '../../../../utils/cashier';
import TransitionalExitPrompt from '../../../TransitionalExitPrompt/TransitionalExitPrompt';

import ExitPrompt from './components/ExitPrompt/ExitPrompt';
import HeaderComponent from './components/HeaderComponent/HeaderComponent';

export interface Buttons {
  hideCloseButton?: boolean;
  back?: boolean;
  onBackClick?: () => void;
  onCloseClick?(event?: React.MouseEvent): void;
  allowMultiClick?: boolean;
  exitWizardOnBackClick?: boolean;
}
interface Props extends TitleProp {
  buttons?: Buttons;
  modal?: boolean;
  showExitPrompt?: boolean;
  withImage?: boolean;
  showSGImage?: boolean;
  showNavigationHeader?: boolean;
  hideProgressBar?: boolean;
}

export interface HeaderPropsProp {
  headerProps: Props;
}

const Header: React.FunctionComponent<Props> = ({
  buttons,
  modal = false,
  showExitPrompt = true,
  ...rest
}) => {
  const exitWizard = useWizardExit();
  const { goPrevious, previous } = useContext(NavigationContext);
  const isMobileEmbedded = useSelector(select.device.isMobileEmbedded);
  const isPoker = useSelector(select.cms.getBrandId) === BrandTypes.POKER_STARS;
  const isMobile = useSelector(select.device.isMobile);
  const isSwcSignupRmfEcopokerLobby = useFeature(FeatureFlag.SwcSignupRmfEcopokerLobby);
  const isSwcSignupRmfEcoEransitionTwo = useFeature(FeatureFlag.SwcSignupRmfEcoEransitionTwo);
  const fromEvent = useFromEvent();
  const { onModalClose, triggerModalClose, isPrompt } = useModal();
  const [backButtonDisabled, setBackButtonDisabled] = useState(false);
  const [exitFlowConfirmation, setExitFlowConfirmation] = useState(false);
  const [ecoTransition, setEcoTransition] = useState(false);
  const [handler, setHandler] = useState(false);
  const { auth } = useDispatch<Dispatch>();
  const isStandAloneRmfMandatory = useSelector(select.account.isStandAloneRmfMandatory);
  const {
    data: { signupPassed, rmfPassed }
  } = useWizardContext();
  const { loading } = useWizardLoading();

  const handleRegulatoryExitFlow = async () => {
    exitWizard();
    if ((signupPassed && !rmfPassed) || isStandAloneRmfMandatory) {
      auth.logout();
    }
  };

  const handleExitRmfFlow = useCallback(() => {
    if (isSwcSignupRmfEcopokerLobby && signupPassed) {
      return setEcoTransition(true);
    }
    exitWizard();
  }, [exitWizard, isSwcSignupRmfEcopokerLobby, signupPassed]);

  const handleBackClick = useCallback(() => {
    setBackButtonDisabled(!buttons?.allowMultiClick);
    if (buttons?.exitWizardOnBackClick) {
      exitWizard();
      return;
    }
    if (buttons?.onBackClick) {
      buttons.onBackClick();
      executeHistoryPush();
      return;
    }
    if (buttons?.back && !buttons.onBackClick && buttons?.onCloseClick) {
      buttons?.onCloseClick();
      executeHistoryPush();
      return;
    }
    window.starsweb?.app?.setBackButton?.(false);
    executeHistoryPush();
    goPrevious();
  }, [buttons, exitWizard, goPrevious]);

  const handleExitPrompt = useCallback(() => {
    showExitPrompt ? setExitFlowConfirmation(true) : handleExitRmfFlow();
  }, [handleExitRmfFlow, showExitPrompt]);

  const handleCloseClick = useCallback(
    (event?: React.MouseEvent) => {
      if (event && buttons?.onCloseClick) {
        buttons.onCloseClick(event);
        return;
      }
      handleExitPrompt();
    },
    [buttons, handleExitPrompt]
  );

  useEffect(() => {
    const listener = () => {
      if (onModalClose && isPrompt) {
        triggerModalClose();
        return;
      }
      if (onModalClose && !isPrompt) {
        triggerModalClose();
        executeHistoryPush();
        return;
      }
      if (!backButtonDisabled && (buttons?.back || (!buttons && previous))) {
        handleBackClick();
        return;
      }
      executeHistoryPush();
      handleCloseClick();
    };

    const timer = setTimeout(() => window.addEventListener('popstate', listener), 0);

    return () => {
      setTimeout(() => window.removeEventListener('popstate', listener), 0);
      clearTimeout(timer);
    };
  }, [
    backButtonDisabled,
    buttons,
    handleBackClick,
    handleCloseClick,
    isMobileEmbedded,
    isPrompt,
    onModalClose,
    previous,
    triggerModalClose
  ]);

  useEffect(() => {
    if (isMobileEmbedded) {
      const displayBackButton = isUndefined(buttons?.back)
        ? (!buttons && previous) || modal
        : buttons?.back;
      window.starsweb?.app?.setBackButton?.(displayBackButton);
      if (!handler) {
        fromEvent(document, 'starsbeforeclose').subscribe((e) => {
          e.preventDefault();
          e.stopImmediatePropagation();
          setHandler(true);
        });
      }
    }
  }, [buttons, fromEvent, handler, isMobileEmbedded, modal, previous]);

  useEffect(() => {
    if (handler) {
      handleCloseClick();
      setHandler(false);
    }
  }, [handleCloseClick, handler]);

  useEffect(() => {
    if (loading) {
      window.starsweb?.app?.setBackButton?.(false);
    } else {
      window.starsweb?.app?.setBackButton?.(true);
    }
    executeHistoryPush();
  }, [loading]);

  return (
    <>
      <Helmet>
        {isSwcSignupRmfEcopokerLobby && (
          <link rel="preload" href={trPromptVideo} type="video/mp4" as="video" />
        )}
        {isSwcSignupRmfEcoEransitionTwo && (
          <link
            rel="preload"
            href={isMobile ? trVideoMobile : trScreenVideo}
            type="video/mp4"
            as="video"
          />
        )}
      </Helmet>
      <HeaderComponent
        buttons={buttons}
        backButtonDisabled={backButtonDisabled || loading}
        modal={modal}
        handleExitPrompt={handleExitPrompt}
        handleCloseClick={handleCloseClick}
        handleBackClick={handleBackClick}
        {...rest}
      />
      {exitFlowConfirmation && (
        <ExitPrompt
          onClose={() => setExitFlowConfirmation(false)}
          handleRegulatoryExitFlow={handleRegulatoryExitFlow}
          handleExitRmfFlow={handleExitRmfFlow}
        />
      )}
      {ecoTransition && signupPassed && isPoker && <TransitionalExitPrompt />}
    </>
  );
};

export default Header;
